import Vue from "vue";
import serviceHandler from "@/helpers/handlers/service";
import usersService from "@/services/users";
import { GTM_EVENT_NAME } from "@/enums/gtm-events";
import { GMB_EXPORT_GROUP_BY } from "@/enums/gmbData.enum";
import { PERIOD } from "@/enums/period.enum";
import fileDownload from "js-file-download";
import METRICS from "@/enums/gmbMetricsUpdated.enum.js";

export class GMBSimpleModel {
  constructor() {
    this.simpleGmbModel = null;
    this.file = null;
    this.exportId = null;
    this.simpleGmbModelOld = null;
    this.fileOld = null;
  }

  async makeGmb(config, downloadFileExtension = null) {
    const apiPayload = this._createGMBPayload(config);

    if (downloadFileExtension === null) {
      // showing data, not downloading
      const gmbDataFromApi = await this._getGMBDataFromApi(apiPayload);
      this.simpleGmbModel = this._createGMBDataModel(gmbDataFromApi);
    } else {
      // GTM event data collection below
      Vue.gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.GMB_ANALYTICS_EXPORT,
        format: downloadFileExtension,
        action:
          config.group === GMB_EXPORT_GROUP_BY.GMB_ADDRESS
            ? "export_by_locations"
            : "export_by_dates"
      });
      // end GTM event stuff

      this.file = await this._getExportedFileFromApi(apiPayload, {
        file_type: downloadFileExtension
      });

      fileDownload(
        this.file,
        `GMB_analysis_export_${config.dateFrom}_${config.dateTo}.${downloadFileExtension}`
      );

      this.file = null;
    }
  }

  async makeGmbAsync(config, downloadFileExtension = null) {
    const apiPayload = this._createGMBPayload(config);

    if (downloadFileExtension === null) {
      // showing data, not downloading
      const gmbDataFromApi = await this._getGMBDataFromApi(apiPayload);
      this.simpleGmbModel = this._createGMBDataModel(gmbDataFromApi);
    } else {
      // GTM event data collection below
      Vue.gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.GMB_ANALYTICS_EXPORT,
        format: downloadFileExtension,
        action:
          config.group === GMB_EXPORT_GROUP_BY.GMB_ADDRESS
            ? "export_by_locations"
            : "export_by_dates"
      });
      // end GTM event stuff
      apiPayload.file_type = downloadFileExtension;
      return await this._getExportedFileFromApiAsync(apiPayload);
    }
  }

  async makeGmbOld(config, downloadFileExtension = null) {
    const apiPayload = this._createGMBPayload(config);

    if (downloadFileExtension === null) {
      // showing data, not downloading
      const gmbDataFromApi = await this._getGMBDataFromApiOld(apiPayload);
      this.simpleGmbModelOld = this._createGMBDataModel(gmbDataFromApi);
    } else {
      // GTM event data collection below
      Vue.gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.GMB_ANALYTICS_EXPORT,
        format: downloadFileExtension,
        action:
          config.group === GMB_EXPORT_GROUP_BY.GMB_ADDRESS
            ? "export_by_locations"
            : "export_by_dates"
      });
      // end GTM event stuff

      this.fileOld = await this._getExportedFileFromApiOld(apiPayload, {
        file_type: downloadFileExtension
      });

      fileDownload(
        this.fileOld,
        `GMB_analysis_export_${config.dateFrom}_${config.dateTo}.${downloadFileExtension}`
      );
      this.fileOld = null;
    }
  }

  returnGmb() {
    return this.simpleGmbModel;
  }

  returnGmbOld() {
    return this.simpleGmbModelOld;
  }

  _createGMBPayload({
    dateFrom,
    dateTo,
    googleImportedAddressesIds,
    metrics,
    group
  }) {
    const allMetrics = metrics.map(metric => {
      return {
        metric: metric,
        group_by: group
      };
    });

    return {
      date_from: dateFrom,
      date_to: dateTo || dateFrom,
      gmb_addresses: googleImportedAddressesIds,
      cities: [],
      metrics: allMetrics
    };
  }

  async _getGMBDataFromApi(apiPayload) {
    return await serviceHandler(usersService.getGoogleInsights(apiPayload));
  }

  async _getExportedFileFromApi(apiPayload, params) {
    return await serviceHandler(
      usersService.getGoogleInsights(apiPayload, params)
    );
  }

  async _getExportedFileFromApiAsync(apiPayload, params) {
    return await serviceHandler(
      usersService.getGoogleInsightsAsync(apiPayload, params)
    );
  }

  async _getGMBDataFromApiOld(apiPayload) {
    return await serviceHandler(usersService.getGoogleInsightsOld(apiPayload));
  }

  async _getExportedFileFromApiOld(apiPayload, params) {
    return await serviceHandler(
      usersService.getGoogleInsightsOld(apiPayload, params)
    );
  }

  _formatNumber(value) {
    if (typeof value === "string" || value instanceof String) return value;
    return new Intl.NumberFormat("fr-FR").format(value);
  }

  _createGMBDataModel(gmbDataFromApi) {
    const combinedViewsStats = this._combineViewsStats(gmbDataFromApi);

    return gmbDataFromApi.map(gmbMetric => {
      const valuesSum = this._setGmbValuesSum(
        gmbMetric.values.map(value => value.value)
      );
      return {
        groupBy: gmbMetric.group_by || PERIOD.DAY,
        metric: gmbMetric.metric || null,
        values: this._setGmbValues(gmbMetric.values),
        conversionValues: this._setGmbConversionValues(
          gmbMetric,
          combinedViewsStats
        ),
        sum: valuesSum
      };
    });
  }

  _setGmbValues(value) {
    return value.map(value => {
      return {
        t: value.date,
        y: value.value
      };
    });
  }

  _setGmbValuesSum(values) {
    return values.reduce((a, b) => a + b, 0);
  }

  _combineViewsStats(gmbDataFromApi) {
    const viewMetrics = gmbDataFromApi.filter(gmbMetric =>
      this.viewsMetrics.includes(gmbMetric.metric)
    );
    const combinedStats = {};
    viewMetrics.forEach(metric => {
      metric.values.forEach(value => {
        if (combinedStats[value.date]) {
          combinedStats[value.date] += value.value;
        } else {
          combinedStats[value.date] = value.value;
        }
      });
    });
    return combinedStats;
  }

  _setGmbConversionValues(gmbMetric, combinedViewsStats) {
    if (!this.conversionMetric.includes(gmbMetric.metric)) {
      return null;
    }
    const conversionValues = gmbMetric.values.map(value => {
      const views = combinedViewsStats[value.date] || value.value;
      return {
        date: value.date,
        value: ((value.value / views) * 100).toFixed(1)
      };
    });

    return this._setGmbValues(conversionValues);
  }

  get viewsMetrics() {
    return [
      METRICS.BUSINESS_IMPRESSIONS_DESKTOP_MAPS,
      METRICS.BUSINESS_IMPRESSIONS_DESKTOP_SEARCH,
      METRICS.BUSINESS_IMPRESSIONS_MOBILE_MAPS,
      METRICS.BUSINESS_IMPRESSIONS_MOBILE_SEARCH
    ];
  }

  get conversionMetric() {
    return [
      METRICS.BUSINESS_DIRECTION_REQUESTS,
      METRICS.CALL_CLICKS,
      METRICS.WEBSITE_CLICKS
    ];
  }
}
